import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eye, CalendarClock, X, Copy, Bold, Italic, Heading2, List, Link2, ImagePlus, Loader2, Send, FlaskConical, Eraser } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { NewsletterTemplates } from './NewsletterTemplates';
import { uploadMemberPhoto, getNewsletters, getNewsletterOpens, sendTestNewsletter, sendNewsletter, cancelNewsletter, API } from '../../lib/api';

const TOOLS = [
  { icon: Bold, cmd: 'bold', label: 'Bold' },
  { icon: Italic, cmd: 'italic', label: 'Italic' },
  { icon: Heading2, cmd: 'formatBlock', arg: 'h2', label: 'Heading (click again for normal text)' },
  { icon: List, cmd: 'insertUnorderedList', label: 'Bullet list' },
  { icon: Eraser, cmd: 'removeFormat', label: 'Clear formatting' },
];

const clean = (html) => DOMPurify.sanitize(html, { FORBID_TAGS: ['style', 'form', 'input'], FORBID_ATTR: ['style'] });
const previewDoc = (html) => `<!doctype html><html><head><style>body{font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#555;margin:8px}img{max-width:100%;height:auto}a{color:#4a9cd4}h2{font-size:20px;color:#2f2f2f}</style></head><body>${clean(html)}</body></html>`;
const absolutize = (html) => html.replace(/src="\/api\//g, `src="${API}/`);
const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';

function Toolbar({ editorRef, onImage, uploading }) {
  const exec = (cmd, arg) => {
    editorRef.current.focus();
    const toggleOff = cmd === 'formatBlock' && document.queryCommandValue('formatBlock').toLowerCase() === arg;
    document.execCommand(cmd, false, toggleOff ? 'p' : arg);
  };
  const addLink = () => {
    const url = window.prompt('Link address (https://...)');
    if (url) exec('createLink', /^https?:\/\//i.test(url) ? url : `https://${url}`);
  };
  const btn = 'p-2 rounded-sm text-[#555] hover:bg-[#eef5fb] hover:text-[#4a9cd4] transition-colors';
  return (
    <div className="flex flex-wrap gap-1 border-b border-neutral-200 p-1.5 bg-[#fafafa]">
      {TOOLS.map((t) => (
        <button key={t.label} type="button" title={t.label} data-testid={`nl-tool-${t.cmd}`} className={btn}
          onMouseDown={(e) => { e.preventDefault(); exec(t.cmd, t.arg); }}><t.icon size={16} /></button>
      ))}
      <button type="button" title="Insert link" data-testid="nl-tool-link" className={btn}
        onMouseDown={(e) => { e.preventDefault(); addLink(); }}><Link2 size={16} /></button>
      <label title="Insert image" data-testid="nl-tool-image" className={`${btn} cursor-pointer`}>
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
        <input type="file" accept="image/*" className="hidden" data-testid="nl-image-input" onChange={onImage} />
      </label>
    </div>
  );
}

const fmtET = (iso) => new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const pad = (v) => String(v).padStart(2, '0');
const SLOTS = Array.from({ length: 96 }, (_, i) => `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`);
const slotLabel = (t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${pad(m)} ${h >= 12 ? 'PM' : 'AM'}`; };
const nextTuesday = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' }));
  d.setDate(d.getDate() + (((2 - d.getDay() + 7) % 7) || 7));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const todayET = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
const scheduleLabel = (date, time) => {
  const [y, mo, d] = date.split('-').map(Number);
  return `${new Date(y, mo - 1, d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })} at ${slotLabel(time)} ET`;
};

function SchedulePicker({ mode, setMode, date, setDate, time, setTime }) {
  const pill = (on) => `font-sans-ah text-[12px] px-3.5 py-2 rounded-full border transition-colors ${on ? 'bg-[#1f2c33] border-[#1f2c33] text-white' : 'border-neutral-300 text-[#555] hover:border-[#4a9cd4]'}`;
  return (
    <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="newsletter-schedule">
      <span className="font-sans-ah text-[12px] tracking-wide-ah text-[#555]">WHEN</span>
      <button type="button" onClick={() => setMode('now')} data-testid="newsletter-when-now" className={pill(mode === 'now')}>Send now</button>
      <button type="button" onClick={() => setMode('later')} data-testid="newsletter-when-later" className={`${pill(mode === 'later')} flex items-center gap-1.5`}><CalendarClock size={14} /> Schedule for later</button>
      {mode === 'later' && (
        <>
          <input type="date" value={date} min={todayET()} onChange={(e) => setDate(e.target.value)} aria-label="Send date" data-testid="newsletter-schedule-date"
            className="border border-neutral-300 rounded-sm px-3 py-2 font-sans-ah text-[13px] focus:outline-none focus:border-[#4a9cd4]" />
          <select value={time} onChange={(e) => setTime(e.target.value)} aria-label="Send time" data-testid="newsletter-schedule-time"
            className="border border-neutral-300 rounded-sm px-3 py-2 font-sans-ah text-[13px] bg-white focus:outline-none focus:border-[#4a9cd4]">
            {SLOTS.map((t) => <option key={t} value={t}>{slotLabel(t)}</option>)}
          </select>
          <span className="font-sans-ah text-[12px] text-[#999]">Ontario time (ET)</span>
        </>
      )}
    </div>
  );
}

function OpenStat({ n }) {
  const rate = n.sent ? Math.min(100, Math.round((100 * (n.opens || 0)) / n.sent)) : 0;
  return (
    <span className="flex items-center gap-1.5 text-[12px] text-[#555]" data-testid={`newsletter-opens-${n.id}`}>
      <Eye size={14} className="text-[#4a9cd4]" /> {n.opens || 0} opened
      <span className="inline-block w-16 h-1.5 bg-neutral-100 rounded-full overflow-hidden"><span className="block h-full bg-[#4a9cd4]" style={{ width: `${rate}%` }} /></span>
      <span className="font-medium">{rate}%</span>
    </span>
  );
}

function Openers({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { getNewsletterOpens(id).then(setRows).catch(() => setRows([])); }, [id]);
  if (!rows) return <Loader2 size={16} className="animate-spin text-[#4a9cd4] my-3" />;
  if (!rows.length) return <p className="font-sans-ah text-[13px] text-[#999] my-3">No opens recorded yet.</p>;
  return (
    <div className="max-h-[220px] overflow-y-auto my-3 border border-neutral-100 rounded-sm" data-testid={`newsletter-openers-${id}`}>
      {rows.map((r) => (
        <div key={r.email} className="flex flex-wrap gap-x-4 px-3 py-2 border-b border-neutral-50 font-sans-ah text-[13px]">
          <span className="flex-1 text-[#2f2f2f]">{r.email}{r.name ? <span className="text-[#999]"> — {r.name}</span> : null}</span>
          <span className="text-[#999]">{r.first_opened_at ? new Date(r.first_opened_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : ''}{r.count > 1 ? ` · ${r.count} views` : ''}</span>
        </div>
      ))}
    </div>
  );
}

function HistoryItem({ n, onCancel, onReuse }) {
  const [open, setOpen] = useState(false);
  const color = { sent: 'text-green-600', sending: 'text-[#4a9cd4]', partial: 'text-amber-600', failed: 'text-red-500', scheduled: 'text-[#8a5cf6]' };
  const scheduled = n.status === 'scheduled';
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)} className="bg-white border border-neutral-100 rounded-sm p-3.5" data-testid={`newsletter-history-${n.id}`}>
      <summary className="cursor-pointer flex flex-wrap items-center gap-3 font-sans-ah text-[14px]">
        <span className="flex-1 text-[#2f2f2f] font-medium">{n.subject}</span>
        <span className="text-[12px] text-[#999]">{(n.created_at || '').slice(0, 16).replace('T', ' ')}</span>
        <button type="button" onClick={(e) => { e.preventDefault(); onReuse(n); }} data-testid={`newsletter-reuse-${n.id}`} title="Copy into the editor"
          className="flex items-center gap-1 text-[12px] text-[#4a9cd4] hover:bg-[#eef4f8] px-2 py-1 rounded-sm"><Copy size={13} /> Reuse</button>
        {scheduled ? (
          <>
            <span className="flex items-center gap-1.5 text-[12px] uppercase tracking-wide-ah text-[#8a5cf6]" data-testid={`newsletter-scheduled-for-${n.id}`}>
              <CalendarClock size={14} /> {n.group && n.group !== 'all' ? `${n.group} · ` : ''}Scheduled · {fmtET(n.scheduled_at)} ET
            </span>
            <button type="button" onClick={(e) => { e.preventDefault(); onCancel(n); }} data-testid={`newsletter-cancel-${n.id}`}
              className="flex items-center gap-1 text-[12px] text-red-500 hover:bg-red-50 px-2 py-1 rounded-sm"><X size={13} /> Cancel</button>
          </>
        ) : (
          <>
            <span className={`text-[12px] uppercase tracking-wide-ah ${color[n.status] || ''}`}>
              {n.group && n.group !== 'all' ? `${n.group} · ` : ''}{n.status} · {n.sent}/{n.recipients}{n.failed ? ` · ${n.failed} failed` : ''}
            </span>
            <OpenStat n={n} />
          </>
        )}
      </summary>
      {open && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          {!scheduled && <><p className="font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase">Who opened it</p><Openers id={n.id} /></>}
          <iframe title={`Preview of ${n.subject}`} sandbox="" srcDoc={previewDoc(n.html)}
            className="w-full h-[480px] bg-white border border-neutral-100" data-testid={`newsletter-preview-${n.id}`} />
        </div>
      )}
    </details>
  );
}

function History({ items, onCancel, onReuse }) {
  if (!items.length) return <p className="font-sans-ah text-[13px] text-[#999]">No newsletters sent yet.</p>;
  return (
    <div className="space-y-2" data-testid="newsletter-history">
      <p className="font-sans-ah text-[12px] text-[#999] mb-2">Open counts are estimates: some email apps block images (undercount) and Apple Mail may pre-load them (overcount).</p>
      {items.map((n) => <HistoryItem key={n.id} n={n} onCancel={onCancel} onReuse={onReuse} />)}
    </div>
  );
}

export const GROUPS = [['members', 'Members'], ['public', 'Public'], ['sponsors', 'Sponsors']];
const AUDIENCE = [['all', 'All subscribers'], ...GROUPS];

export function NewsletterComposer({ subscribers = [] }) {
  const [group, setGroup] = useState('all');
  const active = subscribers.filter((x) => !x.unsubscribed);
  const subscriberCount = group === 'all' ? active.length : active.filter((x) => (x.groups || []).includes(group)).length;
  const audienceName = AUDIENCE.find(([k]) => k === group)[1].toLowerCase();
  const editorRef = useRef(null);
  const composerRef = useRef(null);
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState('');
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState('now');
  const [date, setDate] = useState(nextTuesday);
  const [time, setTime] = useState('09:00');
  const load = useCallback(() => getNewsletters().then(setHistory).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!history.some((h) => h.status === 'sending')) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [history, load]);

  const payload = () => {
    const html = editorRef.current.innerHTML.trim();
    const empty = !editorRef.current.innerText.trim() && !html.includes('<img');
    if (!subject.trim() || empty) { toast.error('Please add a subject and a message'); return null; }
    return { subject: subject.trim(), html: clean(absolutize(html)) };
  };

  const getContent = () => {
    const html = editorRef.current.innerHTML.trim();
    return { subject: subject.trim(), html: clean(absolutize(html)), hasBody: !!editorRef.current.innerText.trim() || html.includes('<img') };
  };
  const loadContent = (subj, html) => {
    setSubject(subj || '');
    editorRef.current.replaceChildren(DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true, FORBID_TAGS: ['style', 'form', 'input'] }));
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const reuse = (n) => {
    const { subject: subj, hasBody } = getContent();
    if ((subj || hasBody) && !window.confirm(`Replace what's in the editor with "${n.subject}"?`)) return;
    loadContent(n.subject, n.html);
    toast.success('Copied into the editor');
  };

  const cancel = async (n) => {
    if (!window.confirm(`Cancel the scheduled newsletter "${n.subject}"?`)) return;
    try { await cancelNewsletter(n.id); toast.success('Scheduled newsletter cancelled'); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadMemberPhoto(file);
      editorRef.current.focus();
      document.execCommand('insertImage', false, url);
    } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
  };

  const test = async () => {
    const p = payload(); if (!p) return;
    setBusy('test');
    try { const r = await sendTestNewsletter(p); toast.success(`Test sent to ${r.to}`); } catch (err) { toast.error(errMsg(err)); }
    setBusy('');
  };

  const later = mode === 'later';
  const send = async () => {
    const p = payload(); if (!p) return;
    const who = `${subscriberCount} subscriber${subscriberCount === 1 ? '' : 's'}${group === 'all' ? '' : ` in ${audienceName}`}`;
    if (later && !date) { toast.error('Please pick a date'); return; }
    const msg = later ? `Schedule "${p.subject}" for ${scheduleLabel(date, time)} to ${who}?` : `Send "${p.subject}" to ${who}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusy('send');
    try {
      await sendNewsletter({ ...p, group, ...(later ? { scheduled_at: `${date}T${time}` } : {}) });
      toast.success(later ? `Scheduled for ${scheduleLabel(date, time)}` : 'Newsletter is being sent');
      setMode('now'); setGroup('all');
      setSubject(''); editorRef.current.replaceChildren();
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy('');
  };

  return (
    <div className="mb-12 scroll-mt-28" ref={composerRef} data-testid="newsletter-composer">
      <h2 className="font-serif-ah text-[26px] text-[#2f2f2f] mb-1">Write a Newsletter</h2>
      <p className="font-sans-ah text-[13px] text-[#888] mb-5">Sent in the Chamber's branded email style. Every email includes a one-click unsubscribe link.</p>
      <NewsletterTemplates getContent={getContent} onUse={loadContent} />
      <label htmlFor="nl-subject" className="font-sans-ah text-[12px] tracking-wide-ah text-[#555] block mb-1.5">SUBJECT</label>
      <input id="nl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
        data-testid="newsletter-subject-input" placeholder="e.g. June Chamber Update"
        className="w-full border border-neutral-300 rounded-sm px-4 py-3 font-sans-ah text-[14px] mb-4 focus:outline-none focus:border-[#4a9cd4]" />
      <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#555] mb-1.5">MESSAGE</p>
      <div className="border border-neutral-300 rounded-sm bg-white focus-within:border-[#4a9cd4] transition-colors">
        <Toolbar editorRef={editorRef} onImage={onImage} uploading={uploading} />
        <div ref={editorRef} contentEditable suppressContentEditableWarning data-testid="newsletter-body-editor"
          className="nl-content min-h-[220px] p-4 font-sans-ah text-[15px] text-[#444] leading-relaxed focus:outline-none" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="newsletter-audience">
        <span className="font-sans-ah text-[12px] tracking-wide-ah text-[#555]">SEND TO</span>
        {AUDIENCE.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setGroup(k)} data-testid={`newsletter-audience-${k}`}
            className={`font-sans-ah text-[12px] px-3.5 py-2 rounded-full border transition-colors ${group === k ? 'bg-[#1f2c33] border-[#1f2c33] text-white' : 'border-neutral-300 text-[#555] hover:border-[#4a9cd4]'}`}>
            {label}
          </button>
        ))}
      </div>
      <SchedulePicker mode={mode} setMode={setMode} date={date} setDate={setDate} time={time} setTime={setTime} />
      {later && <p className="font-sans-ah text-[12px] text-[#999] mt-2">Goes to everyone in the chosen audience at send time, so new subscribers are included and unsubscribes are respected.</p>}
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={test} disabled={!!busy} data-testid="newsletter-test-btn"
          className="font-sans-ah text-[12px] tracking-wide-ah px-5 py-3 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2 disabled:opacity-50">
          {busy === 'test' ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} SEND TEST TO CHAMBER INBOX
        </button>
        <button onClick={send} disabled={!!busy || !subscriberCount} data-testid="newsletter-send-btn"
          className="btn-ah-solid font-sans-ah text-[12px] px-5 py-3 rounded-sm flex items-center gap-2 disabled:opacity-50">
          {busy === 'send' ? <Loader2 size={15} className="animate-spin" /> : later ? <CalendarClock size={15} /> : <Send size={15} />}
          {later ? `SCHEDULE FOR ${date ? scheduleLabel(date, time).toUpperCase() : '…'}` : `SEND TO ${subscriberCount} SUBSCRIBER${subscriberCount === 1 ? '' : 'S'}${group === 'all' ? '' : ` IN ${audienceName.toUpperCase()}`}`}
        </button>
      </div>
      <h3 className="font-serif-ah text-[20px] text-[#2f2f2f] mt-10 mb-4">Scheduled & Sent Newsletters</h3>
      <History items={history} onCancel={cancel} onReuse={reuse} />
    </div>
  );
}
